import { Button, ButtonLevel, ButtonSize, ContextMenu, HoverActionButtonHierarchy } from '@vizit/atoms';
import { Menu, MenuItem, MenuPopover, MenuSeparator, MenuTrigger } from '@vizit/atoms';
import { DotsVerticalIcon, DownloadMinimalIcon, Edit03Icon, Trash01Icon } from '@vizit/icons';
import * as React from 'react';
import { CollectionEditDialog } from '../collection-edit-dialog';
import { DeleteCollectionDialog } from '../delete-collection-dialog';

/**
 * Per-surface treatment for the trigger button. Ghost has no background at
 * rest, so it only holds contrast on solid app surfaces; triggers floated
 * over imagery need the overlay chip to stay legible on arbitrary pixels.
 */
enum CollectionMenuTriggerVariant {
  /** Transparent icon button, for solid surfaces (e.g. the breadcrumb bar). */
  Ghost = 'ghost',
  /** Dark overlay chip, for triggers placed over imagery (e.g. CollectionCard thumbnails). */
  Overlay = 'overlay',
}

interface CollectionMenuDropdownProps {
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * The current name of the collection
   */
  collectionName: string;
  /**
   * The ID of the portfolio
   */
  workspaceId?: string;
  /**
   * The ID of the organization
   */
  organizationId?: string;
  /**
   * Whether to show the trigger button. The trigger stays visible while its
   * menu is open and whenever it holds keyboard focus.
   *
   * @optional
   * @type boolean
   * @default true
   */
  isVisible?: boolean;
  /**
   * Visual treatment for the trigger button, chosen per surface.
   *
   * @optional
   * @type CollectionMenuTriggerVariant
   * @default CollectionMenuTriggerVariant.Ghost
   */
  triggerVariant?: CollectionMenuTriggerVariant;
  /**
   * Callback when the collection is successfully renamed
   * Can be async to support waiting for mutations to complete
   */
  onSuccessfulRename: (newName: string) => void | Promise<void>;
  /**
   * Callback when the collection is successfully deleted
   * Can be async to support waiting for mutations to complete
   */
  onSuccessfulDelete: (collectionId: string) => void | Promise<void>;
  /**
   * Downloads every image of the collection as a zip. Adds "Download collection" between Rename and Delete; only the
   * Collections list passes it (SDD D57, D77)
   *
   * @optional
   */
  onDownload?: () => void;
}

/**
 * Rename / download / delete menu for a collection, opened from an icon-only button whose
 * treatment is chosen per surface (see CollectionMenuTriggerVariant).
 */
function CollectionMenuDropdown(props: CollectionMenuDropdownProps) {
  const {
    collectionId,
    collectionName,
    workspaceId,
    organizationId,
    isVisible = true,
    triggerVariant = CollectionMenuTriggerVariant.Ghost,
    onSuccessfulRename,
    onSuccessfulDelete,
    onDownload,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const handleMenuAction = React.useCallback(
    (key: React.Key) => {
      if (key === 'rename') {
        setIsRenameDialogOpen(true);
      } else if (key === 'download') {
        onDownload?.();
      } else if (key === 'delete') {
        setIsDeleteDialogOpen(true);
      }
    },
    [onDownload],
  );

  const handleDelete = React.useCallback(
    async (id: string) => {
      // Wait for mutation to complete before closing dialog
      await onSuccessfulDelete(id);
      setIsDeleteDialogOpen(false);
    },
    [onSuccessfulDelete],
  );

  const handleRename = React.useCallback(
    async (newName: string) => {
      // Wait for mutation to complete before closing dialog
      await onSuccessfulRename(newName);
      setIsRenameDialogOpen(false);
    },
    [onSuccessfulRename],
  );

  const menuPopover = (
    <MenuPopover>
      <Menu onAction={handleMenuAction}>
        <MenuItem id="rename" textValue="Rename collection">
          <Edit03Icon size={14} />
          Rename collection
        </MenuItem>
        {onDownload && (
          <>
            <MenuItem id="download" textValue="Download collection">
              <DownloadMinimalIcon size={14} />
              Download collection
            </MenuItem>
            <MenuSeparator />
          </>
        )}
        <MenuItem id="delete" textValue="Delete collection">
          <Trash01Icon size={14} />
          Delete collection
        </MenuItem>
      </Menu>
    </MenuPopover>
  );

  return (
    <>
      {triggerVariant === CollectionMenuTriggerVariant.Overlay ? (
        <ContextMenu
          onOpenChange={setIsMenuOpen}
          triggerProps={{
            isHovered: isVisible,
            isMenuOpen,
            hierarchy: HoverActionButtonHierarchy.Secondary,
          }}
        >
          {menuPopover}
        </ContextMenu>
      ) : (
        <MenuTrigger onOpenChange={setIsMenuOpen}>
          <Button
            aria-label="Open menu"
            level={ButtonLevel.Ghost}
            size={ButtonSize.Sm}
            iconStart={<DotsVerticalIcon size={20} />}
            // Keyboard users reach the trigger without hovering; reveal it on
            // focus-visible so it is never an invisible tab stop. Driven from
            // react-aria state rather than a CSS :focus-visible rule so the
            // reveal is observable in jsdom (a static Happo snapshot cannot
            // capture focus).
            className={({ isFocusVisible }) =>
              isVisible || isMenuOpen || isFocusVisible ? 'opacity-100' : 'opacity-0'
            }
          />
          {menuPopover}
        </MenuTrigger>
      )}

      <CollectionEditDialog
        collectionId={collectionId}
        currentName={collectionName}
        workspaceId={workspaceId}
        organizationId={organizationId}
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        onSuccessfulRename={handleRename}
        hideTrigger
      />

      <DeleteCollectionDialog
        collectionId={collectionId}
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
      />
    </>
  );
}

export { CollectionMenuDropdown, CollectionMenuTriggerVariant };
export type { CollectionMenuDropdownProps };
